import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  HeadingText,
  Icon,
  StackLayout,
  queryHelpers,
  useMediaQuery,
} from "@leagueplatform/genesis-core";
import { useIntl } from "@leagueplatform/locales";
import { APP_MANAGED_ROUTES, SIGN_OUT } from "common/app-managed-routes";
import { getMyProfileLink } from "common/my-profile-link";
import { usePlanSwitcherLoginGate } from "hooks/use-plan-switcher-login-gate/use-plan-switcher-login-gate.hook";
import { useJourneyNavEnabled } from "hooks/use-journey-nav-enabled/use-journey-nav-enabled.hook";
import iconHome from "assets/home-icon.svg";
import iconBenefits from "assets/benefits-icon.svg";
import iconCare from "assets/care-icon.svg";
import iconClaims from "assets/claims-icon.svg";
import iconJourney from "assets/journey-icon.svg";

type Variant = "nav" | "plain" | "critical";

type Item = {
  labelKey: `STR_${string}`;
  onClick?: () => void;
  to?: string;
  external?: boolean;
  variant?: Variant;
  iconSrc?: string;
};

type Group = {
  headingKey?: `STR_${string}`;
  items: Item[];
};

type Props = {
  open: boolean;
  onClose: () => void;
};

const PRIVACY_POLICY_URL = "https://www.emblemhealth.com/legal/privacy-policy";

const SettingsMenuRow = ({
  item,
  onClose,
  isMobile = false,
}: {
  item: Item;
  onClose: () => void;
  isMobile?: boolean;
}) => {
  const { formatMessage } = useIntl();
  const label = formatMessage({ id: item.labelKey });
  const variant = item.variant ?? "plain";

  // Mobile plain rows are subdued; desktop plain rows are primary.
  let labelColor: string;
  if (variant === "critical") {
    labelColor = "$onSurfaceTextCritical";
  } else if (variant === "nav" || (variant === "plain" && isMobile)) {
    labelColor = "$onSurfaceTextSubdued";
  } else {
    labelColor = "$onSurfaceTextPrimary";
  }

  const inner = (
    <Box
      css={{
        display: "flex",
        alignItems: "center",
        gap: "$half",
        width: "100%",
      }}
    >
      {item.iconSrc ? (
        <Box
          as="img"
          src={item.iconSrc}
          alt=""
          aria-hidden
          css={{ width: "24px", height: "24px", flexShrink: 0 }}
        />
      ) : null}
      <Box
        as="span"
        css={{
          flex: 1,
          fontSize: "14px",
          fontWeight: 800,
          lineHeight: "20px",
          letterSpacing: "-0.2px",
          color: labelColor,
          whiteSpace: "normal",
        }}
      >
        {label}
      </Box>
    </Box>
  );

  const interactiveCss = {
    display: "flex",
    alignItems: "center",
    width: "100%",
    background: "transparent",
    border: "none",
    padding: 0,
    paddingBlock: variant === "nav" ? "$one" : "$half",
    // WCAG 2.5.5 target size — 44px minimum tap target for all rows.
    minHeight: "44px",
    font: "inherit",
    textAlign: "left" as const,
    textDecoration: "none",
    cursor: "pointer",
    color: "inherit",
    "&:hover span": { textDecoration: "underline" },
    "&:focus-visible": {
      outline: "2px solid $interactiveFocusInner",
      outlineOffset: "2px",
    },
  };

  if (item.to && item.external) {
    return (
      <Box
        as="a"
        href={item.to}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClose}
        css={interactiveCss}
      >
        {inner}
      </Box>
    );
  }

  if (item.to) {
    return (
      <Link
        to={item.to}
        onClick={onClose}
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          minHeight: "44px",
          paddingBlock: variant === "nav" ? 16 : 8,
          textDecoration: "none",
          color: "inherit",
        }}
      >
        {inner}
      </Link>
    );
  }

  return (
    <Box
      as="button"
      type="button"
      onClick={() => {
        item.onClick?.();
        onClose();
      }}
      css={interactiveCss}
    >
      {inner}
    </Box>
  );
};

export const SettingsPanel = ({ open, onClose }: Props) => {
  const { formatMessage } = useIntl();
  const isBelowTablet = useMediaQuery(queryHelpers.down("tablet"));
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileDrawerRef = useRef<HTMLDivElement>(null);

  // Switch Accounts is only offered to members with more than one K-ID —
  // parity with iOS `SettingsPresentationModelMapper.shouldShowSwitchAccounts`
  // (EMBLEM-7088). Shares the single source of truth with the login-gate
  // redirect in `PrivateRoutesContainer` via `usePlanSwitcherLoginGate`;
  // the underlying `useAppInitDetails` query is deduped by react-query so
  // there is no extra network fetch.
  const { hasMultiplePlans } = usePlanSwitcherLoginGate();
  const { enabled: journeyNavEnabled } = useJourneyNavEnabled();
  const myProfileLink = getMyProfileLink();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Desktop dropdown closes on outside click. The header trigger toggles
  // `open` itself, so ignore clicks that originated from the trigger button
  // (identified via aria-haspopup="dialog") to avoid immediate reopen/close.
  useEffect(() => {
    if (!open || isBelowTablet) return undefined;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (dropdownRef.current?.contains(target)) return;
      const el = target as HTMLElement;
      if (el.closest?.('[aria-haspopup="dialog"]')) return;
      onClose();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open, isBelowTablet, onClose]);

  // `aria-modal="true"` on the mobile drawer (unlike the desktop popover,
  // which isn't modal and relies on outside-click instead) means focus must
  // move into it on open, stay trapped there, and return to whatever
  // triggered it on close — otherwise a screen reader or keyboard user can
  // tab straight through to content hidden behind the dark overlay.
  useEffect(() => {
    if (!open || !isBelowTablet) return undefined;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const drawer = mobileDrawerRef.current;

    const getFocusable = () =>
      drawer
        ? Array.from(
            drawer.querySelectorAll<HTMLElement>(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            )
          ).filter((el) => !el.hasAttribute("disabled"))
        : [];

    getFocusable()[0]?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const focusable = getFocusable();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    drawer?.addEventListener("keydown", onKeyDown);
    return () => {
      drawer?.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus();
    };
  }, [open, isBelowTablet]);

  if (!open) return null;

  if (!isBelowTablet) {
    const desktopGroups: Group[] = [
      {
        items: [
          ...(hasMultiplePlans
            ? [
                {
                  labelKey: "STR_SETTINGS_SWITCH_ACCOUNT" as const,
                  to: APP_MANAGED_ROUTES.SWITCH_ACCOUNT,
                },
              ]
            : []),
          {
            labelKey: "STR_SETTINGS_MY_PROFILE",
            to: myProfileLink,
          },
          {
            labelKey: "STR_SETTINGS_COMMUNICATION_PREFERENCES",
            to: APP_MANAGED_ROUTES.COMMUNICATION_PREFERENCES,
          },
          {
            labelKey: "STR_SETTINGS_NOTIFICATION_PREFERENCES",
            to: APP_MANAGED_ROUTES.NOTIFICATIONS,
          },
          {
            labelKey: "STR_SETTINGS_MY_DOCUMENTS",
            to: APP_MANAGED_ROUTES.DOCUMENTS,
          },
        ],
      },
      {
        items: [
          {
            labelKey: "STR_SETTINGS_ABOUT_EMBLEM",
            to: APP_MANAGED_ROUTES.ABOUT,
          },
          {
            labelKey: "STR_SETTINGS_PRIVACY_POLICY",
            to: PRIVACY_POLICY_URL,
            external: true,
          },
        ],
      },
      {
        items: [
          {
            labelKey: "STR_SETTINGS_SIGN_OUT",
            to: SIGN_OUT,
            variant: "critical",
          },
        ],
      },
    ];

    return (
      <Box
        ref={dropdownRef}
        role="dialog"
        aria-label={formatMessage({ id: "STR_SETTINGS" })}
        css={{
          position: "fixed",
          top: "75px",
          right: "$three",
          width: "264px",
          background: "$surfaceBackgroundPrimary",
          borderRadius: "9px",
          boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
          padding: "$oneAndHalf",
          zIndex: "$modal",
          "&::before": {
            content: '""',
            position: "absolute",
            top: "-8px",
            right: "16px",
            width: 0,
            height: 0,
            borderLeft: "8px solid transparent",
            borderRight: "8px solid transparent",
            borderBottom: "8px solid $surfaceBackgroundPrimary",
          },
        }}
      >
        {/* Flat list: items and dividers are siblings so the gap applies
            uniformly above and below each divider. */}
        <StackLayout
          orientation="vertical"
          horizontalAlignment="stretch"
          css={{ gap: "10px", width: "100%" }}
        >
          {desktopGroups.flatMap((group, gIdx) => {
            const rows = group.items.map((item) => (
              <SettingsMenuRow
                key={item.labelKey}
                item={item}
                onClose={onClose}
              />
            ));
            if (gIdx < desktopGroups.length - 1) {
              rows.push(
                <Box
                  key={`divider-after-${group.items[0].labelKey}`}
                  role="separator"
                  css={{
                    height: "1px",
                    background: "$onSurfaceBorderSubdued",
                    width: "100%",
                  }}
                />
              );
            }
            return rows;
          })}
        </StackLayout>
      </Box>
    );
  }

  const mobileGroups: Group[] = [
    {
      items: [
        {
          labelKey: "STR_TAB_HOME",
          to: APP_MANAGED_ROUTES.HOME,
          variant: "nav",
          iconSrc: iconHome,
        },
        {
          labelKey: "STR_TAB_BENEFITS",
          to: APP_MANAGED_ROUTES.BENEFITS,
          variant: "nav",
          iconSrc: iconBenefits,
        },
        {
          labelKey: "STR_TAB_CARE",
          to: APP_MANAGED_ROUTES.CARE,
          variant: "nav",
          iconSrc: iconCare,
        },
        {
          labelKey: "STR_TAB_CLAIMS",
          to: APP_MANAGED_ROUTES.CLAIMS,
          variant: "nav",
          iconSrc: iconClaims,
        },
        ...(journeyNavEnabled
          ? [
              {
                labelKey: "STR_TAB_JOURNEY" as const,
                to: APP_MANAGED_ROUTES.JOURNEY,
                variant: "nav" as const,
                iconSrc: iconJourney,
              },
            ]
          : []),
      ],
    },
    {
      headingKey: "STR_SETTINGS_ACCOUNT_HEADING",
      items: [
        ...(hasMultiplePlans
          ? [
              {
                labelKey: "STR_SETTINGS_SWITCH_ACCOUNT" as const,
                to: APP_MANAGED_ROUTES.SWITCH_ACCOUNT,
              },
            ]
          : []),
        {
          labelKey: "STR_SETTINGS_MY_PROFILE",
          to: myProfileLink,
        },
        {
          labelKey: "STR_SETTINGS_COMMUNICATION_PREFERENCES",
          to: APP_MANAGED_ROUTES.COMMUNICATION_PREFERENCES,
        },
        {
          labelKey: "STR_SETTINGS_NOTIFICATION_PREFERENCES",
          to: APP_MANAGED_ROUTES.NOTIFICATIONS,
        },
        {
          labelKey: "STR_SETTINGS_MY_DOCUMENTS",
          to: APP_MANAGED_ROUTES.DOCUMENTS,
        },
      ],
    },
    {
      headingKey: "STR_SETTINGS_ABOUT_HEADING",
      items: [
        {
          labelKey: "STR_SETTINGS_ABOUT_EMBLEM",
          to: APP_MANAGED_ROUTES.ABOUT,
        },
        {
          labelKey: "STR_SETTINGS_PRIVACY_POLICY",
          to: PRIVACY_POLICY_URL,
          external: true,
        },
      ],
    },
    {
      items: [
        {
          labelKey: "STR_SETTINGS_SIGN_OUT",
          to: SIGN_OUT,
          variant: "critical",
        },
      ],
    },
  ];

  return (
    <>
      <Box
        onClick={onClose}
        css={{
          position: "fixed",
          inset: 0,
          background: "rgba(0, 0, 0, 0.4)",
          zIndex: "$modal",
        }}
        aria-hidden
      />
      <Box
        ref={mobileDrawerRef}
        role="dialog"
        aria-modal="true"
        aria-label={formatMessage({ id: "STR_MENU" })}
        css={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(100vw, 335px)",
          background: "$surfaceBackgroundPrimary",
          borderTopLeftRadius: "9px",
          borderBottomLeftRadius: "9px",
          boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
          border: "1px solid $onSurfaceBorderSubdued",
          zIndex: "$modal",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        <Box
          as="button"
          type="button"
          onClick={onClose}
          aria-label={formatMessage({ id: "STR_CLOSE" })}
          css={{
            position: "absolute",
            top: "$half",
            right: "$half",
            background: "transparent",
            border: "none",
            padding: "$half",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: "$onSurfaceIconPrimary",
          }}
        >
          <Icon icon="tinyClose" size="$one" tint="$onSurfaceIconPrimary" />
        </Box>
        <Box
          css={{
            paddingInline: "$oneAndHalf",
            paddingBlockStart: "48px",
            paddingBlockEnd: "$one",
          }}
        >
          <HeadingText level="2" size="md">
            {formatMessage({ id: "STR_MENU" })}
          </HeadingText>
        </Box>

        <StackLayout
          orientation="vertical"
          horizontalAlignment="stretch"
          css={{
            paddingInline: "$oneAndHalf",
            paddingBlockEnd: "$two",
            gap: "$oneAndHalf",
            width: "100%",
          }}
        >
          {mobileGroups.map((group, gIdx) => (
            <StackLayout
              key={group.headingKey ?? `group-${gIdx}`}
              orientation="vertical"
              horizontalAlignment="stretch"
              css={{
                // Nav rows sit flush (each row's paddingBlock does the spacing);
                // Account/About/Sign-out groups use a 10px inner gap.
                gap: gIdx === 0 ? "0" : "10px",
                width: "100%",
                paddingBlockStart: gIdx > 0 ? "$oneAndHalf" : "0",
                borderBlockStart:
                  gIdx > 0 ? "1px solid $onSurfaceBorderSubdued" : "none",
              }}
            >
              {group.headingKey ? (
                <HeadingText
                  level="3"
                  size="sm"
                  css={{ color: "$onSurfaceTextPrimary" }}
                >
                  {formatMessage({ id: group.headingKey })}
                </HeadingText>
              ) : null}
              {group.items.map((item) => (
                <SettingsMenuRow
                  key={item.labelKey}
                  item={item}
                  onClose={onClose}
                  isMobile
                />
              ))}
            </StackLayout>
          ))}
        </StackLayout>
      </Box>
    </>
  );
};
